import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Observable, Subscription } from 'rxjs';

import { Store } from '@ngrx/store';

import { ToastrService } from 'ngx-toastr';

import { v4 as uuidv4 } from 'uuid';

import { AppState } from '../../../../../../../state';
import { UploadIds, UploadKeys } from '../../../../../../../models/upload';
import { initializeUpload, uploadRequest, uploadReset } from '../../../../../../state/upload/upload.actions';
import {
  selectCompleted,
  selectInProgress,
  selectProgress,
  selectUploaded,
} from '../../../../../../state/upload/upload.selectors';
import { PreviewService } from '../../../../../../services/preview/preview.service';

const SUPPORTED_FILE_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mp3', 'm4a'];
const MAX_VIDEO_AUDIO_SIZE_MB = 3;

@Component({
  selector: 'dashboard-audio-upload',
  templateUrl: './audio-upload.component.html',
  styleUrls: ['./audio-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AudioUploadComponent),
      multi: true,
    },
  ],
})
export class AudioUploadComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() uuid: string;
  @Input() isEdit: boolean;
  @Input() isFileToEdit: boolean;
  @Input() filePathInput: string;
  @Input() keyForUpload: UploadKeys;
  @Input() idsForUpload: UploadIds;
  @Input() isSelectAudioDisabled: boolean;

  AudioForm: FormGroup;

  fileToUpload: File = null;

  uploadCompleted$: Observable<boolean>;
  uploadProgress$: Observable<number>;
  uploadIsInProgress$: Observable<boolean>;
  uploadedFiles$: Observable<File[] | string[]>;
  uploadedFiles: File[] | string[];

  private subscriptions = new Subscription();

  constructor(private formBuilder: FormBuilder, private store: Store<AppState>, private toastrService: ToastrService) {}

  ngOnInit(): void {
    this.AudioForm = this.formBuilder.group({
      filePath: [''],
    });

    if (!this.uuid) {
      this.uuid = uuidv4();
    }

    this.store.dispatch(initializeUpload({ uuid: this.uuid }));

    this.uploadCompleted$ = this.store.select(selectCompleted(this.uuid));
    this.uploadProgress$ = this.store.select(selectProgress(this.uuid));
    this.uploadIsInProgress$ = this.store.select(selectInProgress(this.uuid));
    this.uploadedFiles$ = this.store.select(selectUploaded(this.uuid));

    this.subscriptions.add(
      this.uploadedFiles$.subscribe((uploaded) => {
        let value;
        if (uploaded && uploaded.length > 0) {
          this.uploadedFiles = uploaded;
          value = this.isFileToEdit ? this.filePathInput : this.uploadedFiles[0];
        }
        if (uploaded && uploaded.length === 0) {
          value = null;
        }
        this.writeValue(value);
        this.onChange(value);
      })
    );
  }

  get filePath(): AbstractControl {
    return this.AudioForm.get('filePath');
  }

  writeValue(value) {
    value ? this.filePath.setValue(value) : this.AudioForm.reset();
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  handleFileInput(event) {
    this.fileToUpload = event.target?.files[0];

    if (!this.fileToUpload) {
      return;
    }

    const extension = this.fileToUpload?.name?.split('.').pop();

    if (!SUPPORTED_FILE_EXTENSIONS.includes(extension)) {
      this.toastrService.error(
        `Please select a file with one of the following extensions: ${SUPPORTED_FILE_EXTENSIONS.toString()}`
      );
      this.fileToUpload = null;
      return;
    }

    if (this.fileToUpload.size > MAX_VIDEO_AUDIO_SIZE_MB * 1024 * 1024) {
      this.toastrService.error(`Your file is too big. The size should be max ${MAX_VIDEO_AUDIO_SIZE_MB}MB.`);
      this.fileToUpload = null;
      return;
    }

    this.store.dispatch(
      uploadRequest({
        uuid: this.uuid,
        file: this.fileToUpload,
        keyForUpload: this.keyForUpload,
        idsForUpload: this.idsForUpload,
      })
    );
  }

  resetFile(): void {
    if (this.isEdit) {
      this.isFileToEdit = false;
    }
    this.writeValue(undefined);
    this.store.dispatch(uploadReset({ uuid: this.uuid }));
  }

  get supportedFileExtensions(): string[] {
    const extensions = [];
    SUPPORTED_FILE_EXTENSIONS.forEach((extension) => extensions.push(`.${extension}`));
    return extensions;
  }

  get uploadedFileType(): string {
    return this.filePath?.value?.startsWith('videos') ? 'video' : 'audio';
  }

  ngOnDestroy() {
    this.resetFile();
    this.subscriptions.unsubscribe();
  }
}
